import { useCallback } from 'react';
import { Order } from '../../types/order';
import { userOrdersApi } from '../../services/userOrdersApi';
import { UseUserOrderDataReturn, UseUserOrderStateReturn } from './UserOrderTypes';

/**
 * User Order Data Management Hook
 * Handles data fetching and API operations for user orders
 */

interface UseUserOrderDataProps {
  userId: number | null;
  orderState: UseUserOrderStateReturn;
  fetchOrderStats: () => Promise<void>;
}

export const useUserOrderData = ({
  userId,
  orderState,
  fetchOrderStats,
}: UseUserOrderDataProps): UseUserOrderDataReturn => {
  const { setOrders, setOrdersSummary, setLoading, setError } = orderState;

  // Fetch all user orders
  const fetchUserOrders = useCallback(async () => {
    if (!userId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userOrders = await userOrdersApi.getUserOrders(userId);
      setOrders(userOrders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Siparişler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [userId, setOrders, setLoading, setError]);

  // Fetch user orders summary
  const fetchOrdersSummary = useCallback(
    async (limit: number = 5) => {
      if (!userId) {
        return;
      }

      try {
        const summary = await userOrdersApi.getUserOrdersSummary(userId, limit);
        setOrdersSummary(summary);
      } catch (err) {
        console.error('Sipariş özeti yüklenemedi:', err);
      }
    },
    [userId, setOrdersSummary]
  );

  // Fetch single order detail
  const fetchOrderDetail = useCallback(
    async (orderId: number): Promise<Order | null> => {
      try {
        return await userOrdersApi.getOrderDetail(orderId);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Sipariş detayı yüklenirken hata oluştu');
        return null;
      }
    },
    [setError]
  );

  // Refresh all data
  const refreshData = useCallback(() => {
    if (userId) {
      fetchUserOrders();
      fetchOrdersSummary();
      fetchOrderStats();
    }
  }, [userId, fetchUserOrders, fetchOrdersSummary, fetchOrderStats]);

  return {
    fetchUserOrders,
    fetchOrdersSummary,
    fetchOrderDetail,
    refreshData,
  };
};
